/**
 * Per-user, PER EVENT TYPE persistence for the STATE filter on the maneuver report pages, so
 * switching TACK -> ROUNDUP -> TACK comes back with the chips each type was last given.
 *
 * The state vocabularies are disjoint per maneuver type — tacks and gybes use the ride-height
 * H-ladder (H0/H1/H2/...), round ups use tack geometry (RU / JK / G-RU / RU-T / ...), bear aways
 * another again (BA / CHICAGO / 0 / DEANO / ...) and takeoffs name the type of takeoff, entry
 * state plus build angle (HB-H2 / H2 / LB-H2 / HB-H1 / ...); see
 * server_python/scripts/gp50/maintenance/Maneuvers_update_state.py and
 * utilities.race_utils.getTakeoffState. ONE shared saved list therefore
 * means a type switch either blanks the table (a filter nothing on screen can ever match) or
 * silently throws the selection away. Keyed by event type, each type keeps its own.
 *
 * STATES ONLY. Grades / races / legs / trainingRacing / tackEntry stay in the flat `maneuverFilters`
 * blob that ManeuverSettings' Apply writes — those vocabularies are type-independent and users
 * expect them to hold across a type switch.
 *
 * Stored server-side in the shared user settings blob via persistentSettingsService, NOT in
 * localStorage — the choice should follow the user across machines and browsers.
 *
 * Persistence must never break the page, so every entry point swallows and logs. Callers re-validate
 * what they load: RaceSight discovers a type's state vocabulary from the data (see
 * maneuverStateOptionsService), and a saved state can leave that vocabulary between sessions.
 */

import { persistentSettingsService } from '../services/persistentSettingsService';
import { debug as logDebug } from './console';
import { normalizeManeuverState } from './maneuverFoilingState';

/**
 * Map key for an event type. Reuses normalizeManeuverState (trim + upper) so `tack`, ` TACK ` and
 * `TACK` are one bucket rather than three.
 */
function eventTypeKey(eventType: string | null | undefined): string {
  return normalizeManeuverState(eventType);
}

/**
 * Defensive read: the blob is user-editable JSON that older builds never wrote.
 *
 * Returns `null` for anything that is not an array — "never filtered this type", which is what lets
 * the caller fall back to every state discovered for the type. An EMPTY array is a real value ("no
 * state filter", which is what TAKEOFF gets) and comes back as `[]`, so this can never collapse to
 * an `Array.isArray(x) && x.length > 0` test.
 */
function coerceStates(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) { return null; }
  return raw
    .map((state) => normalizeManeuverState(state))
    // Explicit emptiness test, not truthiness: '0' is a real bear away state.
    .filter((state) => state !== '');
}

/**
 * Last map this module read or wrote, per user+class+project.
 *
 * saveSettings is debounced and merges at the TOP-LEVEL key, so a second save that re-read the map
 * before the first one landed would write a stale map and drop the first type's entry — switch
 * TACK -> GYBE, filter both inside a second, and TACK's chips are gone. Seeding the read-modify-
 * write from what we last wrote closes that window. It is only ever a superset of the server's view
 * for this session, and a fresh load merges the server's view underneath it.
 */
const stateMapCache = new Map<string, Record<string, string[]>>();

function cacheKey(userId: string, className: string, projectId: number): string {
  return `${userId}::${className}::${String(projectId)}`;
}

/** The whole saved map, or {} — shared by the load and the read-modify-write save. */
async function loadStateMap(
  userId: string,
  className: string,
  projectId: number
): Promise<Record<string, string[]>> {
  const settings = await persistentSettingsService.loadSettings(userId, className, projectId);
  const raw: unknown = settings?.maneuverStatesByEventType;
  const fromServer = (raw && typeof raw === 'object' && !Array.isArray(raw))
    ? raw as Record<string, string[]>
    : {};
  const key = cacheKey(userId, className, projectId);
  // Our own pending writes win over a server read that may predate them.
  const merged = { ...fromServer, ...(stateMapCache.get(key) ?? {}) };
  stateMapCache.set(key, merged);
  return merged;
}

/**
 * The saved states for one maneuver type, or null when this user has never filtered that type in
 * this project. Null is meaningful: it is what sends the caller to "all states discovered for this
 * type" rather than to an empty (and therefore inert) filter.
 */
export async function loadManeuverStatesForEventType(
  userId: string,
  className: string,
  projectId: number,
  eventType: string
): Promise<string[] | null> {
  const key = eventTypeKey(eventType);
  if (!key) { return null; }
  try {
    const map = await loadStateMap(userId, className, projectId);
    return coerceStates(map[key]);
  } catch (error) {
    logDebug('maneuverStatePersistence: error loading states', { eventType, error });
    return null;
  }
}

/**
 * In-flight save chain per user+class+project, so saves to one map are strictly ordered.
 *
 * The cache above is necessary but NOT sufficient on its own: a save awaits loadStateMap before it
 * computes and caches its new map, so two saves started in the same tick both read the map before
 * either has written its entry, and the second clobbers the first. Chaining makes the second save's
 * read happen after the first save's cache write, which is the only ordering that keeps both.
 */
const saveChains = new Map<string, Promise<void>>();

/**
 * Save one maneuver type's states.
 *
 * READ-MODIFY-WRITE of the whole map: saveSettings merges at the TOP-LEVEL key, so writing a bare
 * `{ maneuverStatesByEventType: { TACK: ... } }` would replace the map and drop every other type.
 * Serialized per user+class+project — see saveChains.
 */
export async function saveManeuverStatesForEventType(
  userId: string,
  className: string,
  projectId: number,
  eventType: string,
  states: string[]
): Promise<void> {
  const key = eventTypeKey(eventType);
  if (!key) { return; }

  const ck = cacheKey(userId, className, projectId);
  const normalized = (states || [])
    .map((state) => normalizeManeuverState(state))
    .filter((state) => state !== '');

  const run = (saveChains.get(ck) ?? Promise.resolve()).then(async () => {
    try {
      const map = await loadStateMap(userId, className, projectId);
      const next = { ...map, [key]: normalized };
      stateMapCache.set(ck, next);
      await persistentSettingsService.saveSettings(userId, className, projectId, {
        maneuverStatesByEventType: next,
      });
      logDebug('maneuverStatePersistence: saved states', { eventType: key, states: normalized });
    } catch (error) {
      logDebug('maneuverStatePersistence: error saving states', { eventType, error });
    }
  });

  // The chain must never hold a rejection, or every later save for this scope would be skipped.
  // The body above already swallows, so this is belt-and-braces.
  saveChains.set(ck, run.catch(() => undefined));
  return run;
}

/**
 * The flat, pre-per-type `maneuverFilters.states` this user last applied, or null.
 *
 * Back-compat seed only. A user upgrading into this feature has one remembered selection in the old
 * shared blob and no per-type entries at all; without this their first visit to any type would drop
 * it and read as "the app forgot my filter". The seed is deliberately NOT reconciled here —
 * RaceSight discovers a type's vocabulary asynchronously (maneuverStateOptionsService), so the seed
 * is applied and then narrowed by that discovery, which drops anything the type cannot show and
 * falls back to every discovered state when nothing survives.
 */
export async function loadLegacyFlatManeuverStates(
  userId: string,
  className: string,
  projectId: number
): Promise<string[] | null> {
  try {
    const settings = await persistentSettingsService.loadSettings(userId, className, projectId);
    return coerceStates(settings?.maneuverFilters?.states);
  } catch (error) {
    logDebug('maneuverStatePersistence: error loading legacy flat states', error);
    return null;
  }
}

/** Test seam: drop the per-scope caches so one test's writes cannot leak into the next. */
export function __resetManeuverStatePersistenceCaches(): void {
  stateMapCache.clear();
  saveChains.clear();
}
